/* ==========================================================================
   Juo bundle cart sync
   Keeps a Juo Build-a-box bundle consistent inside the cart.

   A bundle is one parent line plus one line per child. The children carry the
   box attributes we add in snippets/juo-bundle-add-to-cart.liquid:

       _box                    box-<PARENT_PRODUCT_ID>
       _parent_product_handle  <PARENT_PRODUCT_HANDLE>
       _parent_product_id      <PARENT_PRODUCT_ID>

   The parent line carries none of those, which is exactly how we recognise it:
   a line whose product id matches a child's `_parent_product_id` and that has
   no `_box` of its own.

   Two jobs:
   1. Sync — after any cart change, every child is set to the quantity of its
      parent (1 child per bundle unit). Remove the parent and its children go
      too. NOTE: whether Juo keeps its discount at parent quantity >= 2 depends
      on the Build-a-box tier setup ("Restrict quantity to match available
      tiers" must be OFF, and the minimum tier must keep matching) — verify in
      the cart whenever the tiers change.
   2. Lock — the children's own quantity buttons and remove link are hidden,
      because changing one on its own would only break the bundle. The class is
      also rendered server-side in snippets/line-item.liquid so the controls
      never flash; the tagging here is just a safety net.

   The sync runs as a reconciler rather than by intercepting clicks, so it is
   correct no matter how the cart changed: drawer, cart page, +/- buttons, the
   remove link, or another script.

   If this file fails to load nothing breaks — the cart simply behaves the way
   it does today (the screenshot case: parent 2, children 1).
   ========================================================================== */
(function () {
  if (window.__juoBundleCartSync) return;
  window.__juoBundleCartSync = true;

  var WARN = "[juo-bundle-cart-sync]";
  var CHILD_CLASS = "juo-bundle-child-line";
  // Cart events arrive in bursts (a quantity click triggers change + refresh +
  // drawer re-render). Debounce so one reconcile runs on the settled cart
  // instead of one per intermediate state.
  var DEBOUNCE_MS = 250;

  var syncing = false;
  var refreshTimer = null;
  // Loop brake state: the serialized plan of the last apply attempt that the
  // cart refused (null = last attempt worked, or nothing attempted yet).
  var lastFailedPlan = null;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  // Reload budget for the cart page. Counted in sessionStorage so it survives
  // the reload itself, and reset once the cart comes back in sync (see
  // reconcile) — a normal correction uses exactly one reload.
  var MAX_SYNC_RELOADS = 2;
  var RELOAD_KEY = "juo-bundle-sync-reloads";

  function syncReloadCount() {
    try {
      return parseInt(window.sessionStorage.getItem(RELOAD_KEY), 10) || 0;
    } catch (error) {
      return 0;
    }
  }

  function allowSyncReload() {
    var count = syncReloadCount();
    if (count >= MAX_SYNC_RELOADS) return false;
    try {
      window.sessionStorage.setItem(RELOAD_KEY, String(count + 1));
    } catch (error) {
      // Private mode without storage: allow the reload, the plan brake still
      // stops a runaway.
    }
    return true;
  }

  function clearSyncReloads() {
    if (syncReloadCount() === 0) return;
    try {
      window.sessionStorage.removeItem(RELOAD_KEY);
    } catch (error) {
      /* nothing to clear */
    }
  }

  function onCartPage() {
    return (
      window.themeVariables &&
      window.themeVariables.settings &&
      window.themeVariables.settings.pageType === "cart"
    );
  }

  /* --- Group the cart into bundles ---------------------------------------- */
  function readBundles(cart) {
    var boxes = {};

    (cart.items || []).forEach(function (item) {
      var props = item.properties || {};
      if (!props._box) return;

      if (!boxes[props._box]) {
        boxes[props._box] = {
          parentProductId: String(props._parent_product_id || ""),
          children: [],
          parent: null,
        };
      }
      boxes[props._box].children.push(item);
    });

    Object.keys(boxes).forEach(function (key) {
      var box = boxes[key];
      box.parent =
        (cart.items || []).filter(function (item) {
          var props = item.properties || {};
          return String(item.product_id) === box.parentProductId && !props._box;
        })[0] || null;
    });

    return boxes;
  }

  /* --- Job 1: keep child quantities equal to the parent -------------------
     Returns the cart it read when the cart was left untouched (so the caller
     can reuse it), or null when changes were applied / nothing could be read. */
  async function reconcile() {
    if (syncing) return null;

    var cart;
    try {
      cart = await (await fetch(root() + "cart.js")).json();
    } catch (error) {
      console.warn(WARN, "could not read the cart:", error);
      return null;
    }

    var boxes = readBundles(cart);
    var keys = Object.keys(boxes);
    if (!keys.length) return cart;

    var updates = {};
    var changed = false;

    keys.forEach(function (key) {
      var box = boxes[key];
      // No parent line left -> the bundle was removed, so drop its children.
      var target = box.parent ? box.parent.quantity : 0;

      box.children.forEach(function (child) {
        if (child.quantity !== target) {
          updates[child.key] = target;
          changed = true;
        }
      });
    });

    // NOTE: duplicate loose lines of the same variant are NOT merged here on
    // purpose. When Juo's 100% box discount lands on a loose line, Shopify's
    // discount engine itself splits that line (e.g. 3 spoons -> 2 + 1 free) to
    // carve out the discounted unit. Merging them back just makes Shopify
    // re-split on the next pass, forever. The display side of this is handled
    // in snippets/line-item.liquid (juo_stolen_bundle_discount).

    if (!changed) {
      lastFailedPlan = null;
      clearSyncReloads(); // cart is in sync again: give back the reload budget
      return cart;
    }

    // Loop brake: if the exact same plan already failed to change anything on
    // the previous pass, applying it again would just refresh the cart forever.
    var planJson = JSON.stringify(updates);
    if (planJson === lastFailedPlan) {
      console.warn(WARN, "cart refuses this sync plan, leaving the cart as is:", planJson);
      return cart;
    }

    syncing = true;
    var anyApplied = false;
    var finalCart = null;
    try {
      // Ask the theme which sections it wants re-rendered along with the cart
      // (the cart drawer registers itself here) — same flow as the theme's own
      // product form, so the cart/update.js response can carry the fresh
      // section HTML and no extra render round-trip is needed.
      var sections = new Set();
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections: sections },
        })
      );
      var sectionsParam = Array.from(sections).join(",");

      // ONE request for all lines. This matters for more than tidiness: Juo's
      // own script watches every `/cart/` request with a PerformanceObserver
      // and answers each one with a /cart.js plus a selling-plans section
      // fetch. A call per line therefore multiplied into a burst big enough to
      // earn a 429 from Shopify. cart/update.js keys by line item key, and the
      // lines we touch always have distinct keys.
      var body = { updates: updates };
      if (sectionsParam) body.sections = sectionsParam;

      var response = await fetch(root() + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        anyApplied = true;
        finalCart = await response.json();
      } else {
        console.warn(
          WARN,
          "cart/update.js " + response.status + ":",
          await response.text()
        );
      }

      if (anyApplied) {
        lastFailedPlan = null;

        if (onCartPage()) {
          // The cart page has no section-rerender path in this theme (its
          // quantity links are full navigations), so a reload is the only way
          // to show the corrected state. Guarded: if the cart keeps coming
          // back out of sync we must NOT keep reloading, or the page hammers
          // the shop until Shopify answers 429.
          if (allowSyncReload()) {
            window.location.reload();
          } else {
            console.warn(
              WARN,
              "cart still out of sync after " +
                MAX_SYNC_RELOADS +
                " reloads; leaving the page as is"
            );
          }
        } else if (finalCart && finalCart.sections) {
          // Fast path: hand the drawer the HTML it asked for, exactly like
          // the theme's own add-to-cart flow does.
          document.documentElement.dispatchEvent(
            new CustomEvent("cart:change", {
              bubbles: true,
              detail: { baseEvent: "juo:bundle-sync", cart: finalCart },
            })
          );
        } else {
          // Fallback: let the drawer refetch its section itself.
          document.documentElement.dispatchEvent(
            new CustomEvent("cart:refresh", { bubbles: true })
          );
        }
      } else {
        lastFailedPlan = planJson;
      }
    } catch (error) {
      lastFailedPlan = planJson;
      console.warn(WARN, "could not sync the bundle quantities:", error);
    } finally {
      syncing = false;
    }

    return null;
  }

  /* --- Job 2: hide the bundle lines' manual controls ----------------------- */
  function tagLine(lineKey, className) {
    // The drawer and the cart page can both be in the DOM at once, so tag
    // every instance of the line, not just the first.
    var inputs = document.querySelectorAll(
      '[data-line-key="' + CSS.escape(lineKey) + '"]'
    );
    Array.prototype.forEach.call(inputs, function (input) {
      var line = input.closest("line-item");
      if (line) line.classList.add(className);
    });
  }

  function lockChildLines(cart) {
    var boxes = readBundles(cart);

    Object.keys(boxes).forEach(function (key) {
      boxes[key].children.forEach(function (child) {
        tagLine(child.key, CHILD_CLASS);
      });
    });
  }

  async function refresh() {
    // reconcile() hands back the cart when it left it untouched; when it DID
    // apply changes it triggers a re-render, which fires the cart events and
    // queues the next refresh — that pass locks against the fresh DOM.
    var cart = await reconcile();
    if (cart) lockChildLines(cart);
  }

  /* --- Animated open/close of the "View full bundle" disclosure ------------
     Closed = fully collapsed (grid-template-rows: 0fr), open = expanded, the
     toggle sits under the items. The native <details> toggle is intercepted:
     the `open` attribute goes on immediately so the content renders, the
     `is-open` class then drives the transition, and on close the attribute
     comes off only after the transition ends. Without this script the
     `[open]:not(.juo-js)` CSS keeps the native, non-animated behaviour. */
  document.addEventListener("click", function (event) {
    var toggle = event.target.closest(".juo-bundle-contents__toggle");
    if (!toggle) return;
    var details = toggle.closest("details.juo-bundle-contents");
    if (!details) return;

    event.preventDefault();
    details.classList.add("juo-js");

    if (details.classList.contains("is-open")) {
      details.classList.remove("is-open");
      toggle.setAttribute("aria-expanded", "false");
    } else {
      details.open = true;
      void details.offsetHeight; // paint the collapsed frame first, or nothing animates
      details.classList.add("is-open");
      toggle.setAttribute("aria-expanded", "true");
    }
  });

  document.addEventListener("transitionend", function (event) {
    if (event.propertyName !== "grid-template-rows") return;
    var panel = event.target;
    if (!panel.classList || !panel.classList.contains("juo-bundle-contents__panel")) return;
    var details = panel.closest("details.juo-bundle-contents");
    if (details && !details.classList.contains("is-open")) {
      details.open = false;
    }
  });

  function queueRefresh(event) {
    // Ignore the cart:change we dispatch ourselves after a sync: it only
    // re-renders the drawer with a cart we already reconciled, so reacting to
    // it would cost another /cart.js (and another Juo round-trip) for nothing.
    if (event && event.detail && event.detail.baseEvent === "juo:bundle-sync") {
      return;
    }
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      refreshTimer = null;
      refresh();
    }, DEBOUNCE_MS);
  }

  ["cart:change", "cart:refresh", "cart-drawer:refreshed"].forEach(function (name) {
    document.addEventListener(name, queueRefresh);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", refresh);
  } else {
    refresh();
  }
})();
